<template>
  <v-card class="eie-assets">
    <v-card-title class="toolbar primary white--text">
      <h3 class="mr-3">
        Assets ({{ assets && assets.length || 0 }})
      </h3>
      <v-spacer />
      <div class="ml-5">
        <v-select
          v-model="searchColumn"
          class="ml-5 mr-3"
          dark
          :items="searchColumnOptions"
          label="Search by"
          item-text="title"
          item-value="value"
          hide-details
          @change="onChangePagination"
        />
      </div>

      <v-autocomplete
        v-if="searchColumn === 'assetType'"
        v-model="selectedValue"
        :items="assetTypeOptions"
        :label="`Search by Asset type`"
        primary
        dark
        solo-inverted
        flat
        hide-details
      />
      <v-text-field
        v-else
        v-model="selectedValue"
        primary
        dark
        solo-inverted
        flat
        append-icon="search"
        :label="`Search by ${searchColumn}`"
        :disabled="searchColumn === 'public'"
        single-line
        hide-details
      />
      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            dark
            flat
            icon
            @click="clearEIEAssetsData().then(onChangePagination)"
            v-on="on"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh assets</span>
      </v-tooltip>
    </v-card-title>
    <!-- data table results filters -->
    <v-card-title class="filters grey lighten-5">
      <div class="filters__header">
        <v-tooltip
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              class="ma-0"
              :color="!hasFilters ? 'secondary' : 'primary'"
              v-on="on"
              @click="() => hasFilters ? resetFilters() : null"
            >
              <v-icon>
                {{ hasFilters ? 'restore' : 'filter_list' }}
              </v-icon>
            </v-btn>
          </template>
          <span>{{ hasFilters ? 'Reset ' : '' }}Filters</span>
        </v-tooltip>
      </div>
      <v-select
        v-model="filters.status"
        class="ma-1"
        :items="statusOptions"
        label="Status"
        item-text="title"
        item-value="status"
        item-key="status"
        multiple
        hide-details
      />
      <v-select
        v-model="filters.instrument"
        class="ma-1"
        :items="instrumentOptions"
        label="Instrument"
        item-text="title"
        item-value="value"
        multiple
        hide-details
      />
      <v-select
        v-model="filters.security_type"
        class="ma-1"
        :items="securityTypeOptions"
        label="Security Type"
        item-text="title"
        item-value="value"
        item-key="id"
        multiple
        hide-details
      />
      <v-text-field
        v-model="filters.search"
        class="ma-1"
        append-icon="search"
        label="Filter results"
        hide-details
      />
    </v-card-title>

    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredAssets"
      :pagination.sync="pagination"
      :total-items="totalElements"
      :rows-per-page-items="rowsPerPageItems"
      :loading="loading"
      select-all
      item-key="id"
      class="elevation-1"
    >
      <template
        slot="headers"
        slot-scope="props"
      >
        <tr>
          <th
            v-for="header in props.headers"
            :key="header.text"
            :class="[
              'column sortable',
              pagination.descending ? 'desc' : 'asc',
              header.value === pagination.sortBy ? 'active' : ''
            ]"
            @click="changeSort(header.value)"
          >
            <v-icon small>
              arrow_upward
            </v-icon>
            {{ header.text }}
          </th>
        </tr>
      </template>
      <template
        slot="items"
        slot-scope="props"
      >
        <tr :key="`${props.item.id}${props.item.isPublic}`">
          <td class="text-xs-center">
            {{ props.item.id }}
          </td>
          <td class="text-xs-center">
            {{ props.item.name }}
          </td>
          <td class="text-xs-center">
            {{ props.item.instrument }}
          </td>
          <td class="text-xs-center">
            {{ props.item.securityIdType }}
          </td>
          <td class="text-xs-center">
            <v-chip
              :color="getStatus(props.item.status).color"
              outline
              small
            >
              {{ getStatus(props.item.status).title || 'Unknown' }}
              <v-icon
                v-if="!getStatus(props.item.status).title"
                small
                color="error"
                class="ml-1"
              >
                error
              </v-icon>
            </v-chip>
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.createdAt) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.createdAt) }}
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <!-- toggle asset isPublic on EIE -->
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-switch
                  :input-value="props.item.isPublic === 'true'"
                  :loading="itemUpdating && itemUpdating.id === props.item.id"
                  color="success"
                  class="ma-0 ml-4 pa-0"
                  hide-details
                  v-on="on"
                  @click.stop="togglePublic(props.item)"
                />
              </template>
              <span>{{ props.item.isPublic === 'true' ? 'Hide Asset in EIE' : 'Show Asset in EIE' }}</span>
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <!-- open asset detail -->
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  flat
                  icon
                  :color="props.expanded ? 'blue darken-1' : ''"
                  @click="onAssetSelect(props);"
                  v-on="on"
                >
                  <v-icon small>
                    {{ props.expanded ? 'visibility_off' : 'visibility' }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ props.expanded ? 'Close Asset details' : 'Open Asset details' }}</span>
            </v-tooltip>
            <!--
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  flat
                  icon
                  @click="onFinancialProductLink(props);"
                  v-on="on"
                >
                  <v-icon small>
                    link
                  </v-icon>
                </v-btn>
              </template>
              <span>View EIE PDP</span>
            </v-tooltip>
            -->
          </td>
        </tr>
        <tr v-if="props.item.error">
          <td
            colspan="9"
            class="text-xs-left errorTd"
          >
            <div class="errorRow">
              <v-icon color="red">
                info
              </v-icon> {{ props.item.error }}
            </div>
          </td>
        </tr>
      </template>

      <!-- asset detail opened -->
      <template v-slot:expand="props">
        <v-container
          cols="12"
          class="d-flex grey lighten-2"
        >
          <v-layout
            row
            wrap
          >
            <v-card
              class="ma-2 pa-6"
              cols="12"
            >
              <v-card-title class="toolbar primary white--text subheading">
                <v-icon
                  dark
                  color="white"
                >
                  home_work
                </v-icon>
                <span class="ml-2">Asset details - <strong>{{ props.item.name }}</strong> ({{ props.item.id }})</span>
              </v-card-title>

              <v-card-text>
                <v-layout
                  wrap
                  class="pa-0"
                >
                  <v-flex
                    v-for="field in cleanFields(props.item)"
                    :key="field"
                    class="pa-2"
                  >
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <v-text-field
                            :label="formatFieldName(field, props.item)"
                            :value="formatValue(props.item[field], field)"
                            readonly
                            class="body-1"
                            :class="field"
                          />
                        </span>
                      </template>
                      <span>
                        field: {{ field }}
                      </span>
                    </v-tooltip>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
            <!-- asset tranches -->
            <v-flex>
              <v-card
                class="asset-tranches ma-2 pa-6"
                cols="12"
                flex-grow
              >
                <v-card-title class="toolbar primary white--text subheading">
                  <v-icon
                    dark
                    color="white"
                  >
                    holiday_village
                  </v-icon>
                  <span class="ml-2">Asset Tranches</span>
                </v-card-title>

                <v-card-text class="ma-0 pa-0">
                  <v-data-table
                    :headers="tranchesHeaders"
                    :items="filteredTranches"
                    item-key="id"
                    class="elevation-1"
                    hide-actions
                  >
                    <template
                      slot="items"
                      slot-scope="props"
                    >
                      <tr :key="`${props.item.id}${props.item.isPublic}`">
                        <td class="text-xs-center">
                          {{ props.item.id }}
                        </td>
                        <td class="text-xs-center">
                          {{ props.item.title }}
                        </td>
                        <td class="">
                          {{ humanReadableNumber(props.item.reservedPieces) }}
                          of
                          {{ humanReadableNumber(props.item.existingPieces) }}
                          funded ({{ progress(props.item) }} %)
                          <br>
                          Available: {{ humanReadableNumber(props.item.remainingPieces) }} Pieces
                          <v-progress-linear
                            :color="progressColor(props.item)"
                            height="5"
                            :value="progress(props.item)"
                          />
                        </td>
                        <td class="text-xs-center">
                          Notional: {{ props.item.notionalValue }}
                          <br>
                          Piece Value: {{ props.item.pieceValue }}
                        </td>
                        <td class="text-xs-center">
                          {{ props.item.minInvestmentAmount }}
                        </td>
                        <td class="text-xs-center">
                          {{ props.item.firstPaybackAt }}
                        </td>
                        <td class="text-xs-center">
                          <v-tooltip
                            top
                            dark
                          >
                            <template v-slot:activator="{ on }">
                              <v-chip
                                color="grey"
                                outline
                                small
                                v-on="on"
                              >
                                <span>
                                  {{ formatDateSmall(props.item.createdAt) }}
                                </span>
                              </v-chip>
                            </template>
                            {{ formatDate(props.item.createdAt) }}
                          </v-tooltip>
                        </td>
                        <td class="text-xs-center">
                          <!-- toggle asset isPublic on EIE -->
                          <v-tooltip
                            top
                            dark
                          >
                            <template v-slot:activator="{ on }">
                              <v-switch
                                :input-value="props.item.isPublic === 'true'"
                                :loading="itemUpdating && itemUpdating.id === props.item.id"
                                color="success"
                                class="ma-0 ml-4 pa-0"
                                hide-details
                                v-on="on"
                                @click.stop="togglePublic(props.item)"
                              />
                            </template>
                            <span>{{ props.item.isPublic === 'true' ? 'Hide Asset in EIE' : 'Show Asset in EIE' }}</span>
                          </v-tooltip>
                        </td>
                        <td class="text-xs-center">
                          <!-- toggle asset isPublic on EIE -->
                          <v-tooltip
                            top
                            dark
                          >
                            <template v-slot:activator="{ on }">
                              <v-switch
                                :input-value="props.item.isInvestable === 'true'"
                                :loading="itemUpdating && itemUpdating.id === props.item.id"
                                color="success"
                                class="ma-0 ml-4 pa-0"
                                hide-details
                                v-on="on"
                                @click.stop="toggleInvestable(props.item)"
                              />
                            </template>
                            <span>{{ props.item.isInvestable === 'true' ? 'Disable investment in EIE' : 'Enable investment in EIE' }}</span>
                          </v-tooltip>
                        </td>
                        <td class="text-xs-center">
                          <v-tooltip
                            top
                            dark
                          >
                            <template v-slot:activator="{ on }">
                              <v-btn
                                flat
                                icon
                                @click="onTrancheLink(props);"
                                v-on="on"
                              >
                                <v-icon small>
                                  link
                                </v-icon>
                              </v-btn>
                            </template>
                            <span>View EIE PDP</span>
                          </v-tooltip>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>
        </v-container>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import { setQuery } from '@/helpers';
import EieAssetStatus from '@/enum/eie/EieAssetStatus';
import EieAssetInstrument from '@/enum/eie/EieAssetInstrument';
import EieAssetSecurityType from '@/enum/eie/EieAssetSecurityType';

export default {
  name: 'EieAssetsTable',
  props: {
    assets: {
      type: Array,
      default: () => [],
    },
    tranches: {
      type: Array,
      default: () => [],
    },
    totalElements: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selected: [],
      selectedAsset: null,
      selectedValue: '',
      filteredAssets: [],
      filteredTranches: [],
      fetchTimeout: null,
      itemUpdating: null,
      confirmDialogOpen: false,
      searchColumn: '',
      searchColumnOptions: [
        { title: 'Public Assets', value: 'public' },
        { title: 'Asset Type', value: 'assetType' },
        { title: 'Financial Product Id', value: 'fpId' },
      ],
      statusOptions: Object.keys(EieAssetStatus).map(key => EieAssetStatus[key]),
      securityTypeOptions: Object.keys(EieAssetSecurityType).map(key => EieAssetSecurityType[key]),
      instrumentOptions: Object.keys(EieAssetInstrument).map(key => EieAssetInstrument[key]),
      assetTypeOptions: ['financing', 'yield'],
      pagination: {
        page: 1,
        rowsPerPage: 25,
        sortBy: 'createdAt',
        descending: true,
      },
      rowsPerPageItems: [
        {
          text: '5',
          value: 5,
        },
        {
          text: '10',
          value: 10,
        },
        {
          text: '25',
          value: 25,
        },
        {
          text: '50',
          value: 50,
        },
        {
          text: '100',
          value: 100,
        },
      ],
      filters: {
        search: '',
        status: [],
        instrument: [],
        security_type: [],
      },
      headers: [
        {
          text: 'Financial Product Id',
          align: 'left',
          sortable: true,
          value: 'id',
        },
        {
          text: 'Name',
          align: 'center',
          sortable: true,
          value: 'name',
        },
        {
          text: 'Instrument',
          align: 'center',
          sortable: true,
          value: 'instrument',
        },
        {
          text: 'Security Type',
          align: 'center',
          sortable: true,
          value: 'securityIdType',
        },
        {
          text: 'Status',
          align: 'center',
          sortable: true,
          value: 'status',
        },
        {
          text: 'Created At',
          align: 'center',
          sortable: true,
          value: 'createdAt',
        },
        {
          text: 'is Public',
          align: 'center',
          sortable: true,
          value: 'isPublic',
        },
        {
          text: 'Actions',
          sortable: false,
        },
      ],
      tranchesHeaders: [
        {
          text: 'Tranche Id',
          align: 'left',
          sortable: true,
          value: 'id',
        },
        {
          text: 'Title',
          align: 'center',
          sortable: true,
          value: 'title',
        },
        {
          text: 'Fundingstatus',
          align: 'center',
        },
        {
          text: 'Notional & Piece Value',
          align: 'left',
          sortable: true,
          value: 'notionalValue',
        },
        {
          text: 'Min investment amount',
          align: 'center',
          sortable: true,
          value: 'minInvestmentAmount',
        },
        {
          text: 'First payback at',
          align: 'center',
          sortable: true,
          value: 'firstPaybackAt',
        },
        {
          text: 'Created At',
          align: 'center',
          sortable: true,
          value: 'createdAt',
        },
        {
          text: 'is Public',
          align: 'center',
          sortable: true,
          value: 'isPublic',
        },
        {
          text: 'is Investable',
          align: 'center',
          sortable: true,
          value: 'isInvestable',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
        },
      ],
    };
  },
  computed: {
    hasFilters() {
      return (
        this.filters.status.length > 0 ||
        this.filters.instrument.length > 0 ||
        this.filters.security_type.length > 0 ||
        this.filters.search.length > 0
      );
    },
  },
  watch: {
    assets() {
      this.filterAssets();
      this.$forceUpdate();
    },
    searchColumn(newVal, oldVal) {
      clearTimeout(this.fetchTimeout);

      if (!!oldVal && !!newVal) {
        this.selectedValue = null;
      }
      if (newVal === 'public') {
        this.selectedValue = 'public';
      }
      if (newVal === 'fpId') {
        this.selectedValue = null;
      }
      this.resetFilters();
    },
    filters: {
      handler(val) {
        setQuery(
          {
            search: val.search,
          },
          this.$route,
          this.$router,
        );

        this.filterAssets();
      },
      deep: true,
    },
    selectedValue(newVal) {
      clearTimeout(this.fetchTimeout);

      if (newVal) {
        this.fetchTimeout = setTimeout(async () => {
          this.clearEIEAssetsData().then(this.onChangePagination());
        }, 500);
      }
    },
    pagination() {
      this.onChangePagination();
    },
  },
  beforeMount() {
    this.pagination = {
      page: Number(this.$route.query.page) || 1,
      rowsPerPage: Number(this.$route.query.rowsPerPage) || 25,
      sortBy: this.$route.query.sortBy || 'createdAt',
      descending: Boolean(this.$route.query.descending) || true,
    };
    this.filters.search = this.$route.query.search || '';
    this.searchColumn = this.$route.query.searchColumn || 'public';
    this.selectedValue = this.$route.query.selectedValue || 'public';
  },
  mounted() {
    this.filterAssets();
  },
  methods: {
    ...mapActions(['fetchEIEAssets', 'clearEIEAssetsData', 'patchEIEItem']),
    onAssetSelect(props) {
      props.expanded = !props.expanded;
      this.selectedAsset = props.item;
      this.filterAssets();
    },
    onTrancheLink(props) {
      const tranche = props.item;
      window.open(`https://app.exporo.de/pdp?trancheId=${tranche.id}&view=financing`);
    },
    onFinancialProductLink(props) {
      const fp = props.item;
      window.open(`https://app.exporo.de/pdp?financialProductId=${fp.id}&view=financing`);
    },
    filterAssets() {
      this.filteredAssets = !this.hasFilters
        ? [...this.assets]
        : this.assets
          // check for searchText
          .filter(item => JSON.stringify(item).toLowerCase().includes(this.filters.search.toLowerCase()))
          // check for selected filters
          .filter((item) => {
            let hasStatus = false;
            let hasInstrument = false;
            let hasSecurityType = false;

            // status
            if (!!this.filters.status.length > 0) {
              this.filters.status.forEach((option) => {
                if (item.status === option) {
                  hasStatus = true;
                }
              });
            } else {
              hasStatus = true;
            }

            // instrument
            if (!!this.filters.instrument.length > 0) {
              this.filters.instrument.forEach((option) => {
                if (item.instrument === option) {
                  hasInstrument = true;
                }
              });
            } else {
              hasInstrument = true;
            }

            // securityIdType
            if (!!this.filters.security_type.length > 0) {
              this.filters.security_type.forEach((option) => {
                if (item.securityIdType === option) {
                  hasSecurityType = true;
                }
              });
            } else {
              hasSecurityType = true;
            }

            return hasStatus && hasInstrument && hasSecurityType;
          });
      this.filteredTranches = !this.selectedAsset
        ? [...this.tranches]
        : this.tranches.filter(tranche => tranche.financialProductId === this.selectedAsset.id);
    },
    formatDate(date) {
      return this.$moment(date).format('MMMM Do YYYY, h:mm:ss a');
    },
    formatDateSmall(date) {
      return this.$moment(date).format('DD/MM/YYYY');
    },
    getStatus(status) {
      return this.statusOptions.find(item => item.status === status) || {};
    },
    onChangePagination() {
      // if no values no point in doing new requests
      if (!this.selectedValue || !this.searchColumn) {
        clearTimeout(this.fetchTimeout);

        return;
      }

      setQuery(
        {
          searchColumn: this.searchColumn,
          selectedValue: this.selectedValue,
          search: this.filters.search,
          ...this.pagination,
        },
        this.$route,
        this.$router,
      );

      const params = {
      };

      if (this.searchColumn === 'public') {
        params.isPublic = true;
      } else if (this.searchColumn === 'fpId') {
        params.id = this.selectedValue;
      } else {
        params.assetType = this.selectedValue;
      }

      this.fetchTimeout = setTimeout(async () => {
        await this.fetchEIEAssets(params);
        this.filterAssets();
      }, 1000);
    },
    async togglePublic(item) {
      const isPublic = item.isPublic === 'true';
      this.itemUpdating = item;

      await this.patchEIEItem({
        id: item.id,
        isPublic: `${!isPublic}`,
      });
      this.filterAssets();

      await this.$nextTick();
      this.itemUpdating = null;
    },
    async toggleInvestable(item) {
      const isInvestable = item.isInvestable === 'true';
      this.itemUpdating = item;

      await this.patchEIEItem({
        id: item.id,
        isInvestable: `${!isInvestable}`,
      });
      this.filterAssets();

      await this.$nextTick();
      this.itemUpdating = null;
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
      this.onChangePagination();
    },
    resetFilters() {
      this.filters.status = [];
      this.filters.instrument = [];
      this.filters.security_type = [];
      this.filters.search = '';
    },
    cleanFields(data) {
      return Object.keys(data)
        .filter(field => !!data[field] && typeof data[field] !== 'object');
    },
    formatFieldName(str, item) {
      const camelCaseSentence = str.replace(/([-_]\w)/g, g => g[1].toUpperCase());
      const sentence = _.startCase(camelCaseSentence);

      if (item) {
        if (str === 'security_wallet' && item.sell_offer.asset.provider === 'baaderbank') {
          return `${_.capitalize(sentence)} / Baader Bank account`;
        }
      }

      return _.capitalize(sentence);
    },

    formatValue(value, key) {
      if (key === 'total' || key === 'unit_price') {
        return this.centsToEuros(value);
      }
      if (key.includes('_at')) {
        return this.formatDate(value);
      }

      return value;
    },

    progress(item) {
      const progress = Math.round(100 / item.existingPieces * item.reservedPieces);

      return progress || 0;
    },

    progressColor(item) {
      const progressValue = this.progress(item);
      if (item.existingPieces === 0 || progressValue === 0) {
        return 'grey';
      }
      if (progressValue < 0) {
        return 'error';
      }
      if (progressValue < 50) {
        return 'warning';
      }
      if (progressValue < 90) {
        return 'lime';
      }

      return 'success';
    },

    humanReadableNumber(number) {
      return number ? number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.eie-assets {
  .toolbar {
    position: relative;
    padding: 24px 24px 16px 24px;
  }
  .errorRow {
    width: 98%;
    position: absolute;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    float: left;
  }

  .errorTd {
    padding-bottom: 32px !important;
    .v-icon {
      vertical-align: middle;
    }
  }

  a {
    text-decoration: none;
    color: black;
  }

  .toolbar {
    background-color: #1a4c7d;
    color: white;
  }
  label.v-label.v-label--active.theme--dark.primary--text {
      color: white !important;
  }
  .infoLabel {
    margin-left: 16px;
  }

  .countLabel {
    color: #fff;
  }
  table.v-table tbody td,
  .v-datatable thead th {
      padding: 0 12px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/eie/assets/EieAssetsTable.vue